import { Component, type ReactNode } from 'react'

interface Props { children: ReactNode; fallback?: ReactNode }
interface State { hasError: boolean; error: Error | null }

/** A failed dynamic-import (lazy route chunk) — happens when a new deploy
 *  replaces the hashed JS chunks while the user has the old index.html cached.
 *  The fix is just to reload to fetch the fresh chunk manifest. */
function isChunkLoadError(err: Error | null): boolean {
  const m = (err?.message || '').toLowerCase()
  return (
    m.includes('failed to fetch dynamically imported module') ||
    m.includes('error loading dynamically imported module') ||
    m.includes('importing a module script failed') ||
    err?.name === 'ChunkLoadError'
  )
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error) {
    // Auto-recover from a stale-deploy chunk error: reload ONCE (guarded by
    // sessionStorage so a genuinely-missing chunk can't loop forever).
    if (isChunkLoadError(error)) {
      const KEY = 'chunk_reload_at'
      const last = Number(sessionStorage.getItem(KEY) || 0)
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(KEY, String(Date.now()))
        window.location.reload()
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // Chunk error mid-reload — show a quiet "updating" state, not a red error.
      if (isChunkLoadError(this.state.error)) {
        return (
          <div className="text-center py-12 text-text-muted text-sm">
            Updating to the latest version…
            <div className="mt-3">
              <button type="button" onClick={() => window.location.reload()} className="btn-secondary">Reload now</button>
            </div>
          </div>
        )
      }
      return this.props.fallback ?? (
        <div className="text-center py-12">
          <h2 className="text-xl text-rose-400 mb-2">Something went wrong</h2>
          <p className="text-text-muted text-sm">{this.state.error?.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-secondary mt-4"
          >
            Reload Page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
